import { useState } from "react";
import { AlertTriangle, FileCheck2, Info, Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import type { components } from "@dolfin/api-public";

export type SendPlan = components["schemas"]["InvoiceSendPlan"];
export type SendFix = NonNullable<components["schemas"]["InvoiceSendProblem"]["fix"]>;

const NETWORK_LABEL: Record<string, string> = {
  Sdi: "SDI",
  Peppol: "Peppol",
};

export function networkLabel(network?: string | null) {
  return (network && NETWORK_LABEL[network]) ?? network ?? "the e-invoicing network";
}

function deliveryLabel(state: string) {
  switch (state) {
    case "Delivered":
      return "delivered";
    case "MadeAvailable":
      return "issued, not delivered";
    case "DeliveryFailed":
      return "failed — fix it from the delivery card";
    case "Accepted":
      return "awaiting the network's verdict";
    default:
      return "being sent";
  }
}

const FIX_LABEL: Record<SendFix, string> = {
  Customer: "Edit customer",
  Organisation: "E-invoicing settings",
  Invoice: "Edit invoice",
};

interface Props {
  plan: SendPlan | null;
  loading: boolean;
  error: string | null;
  /** Re-runs the plan, after the user has fixed something elsewhere. */
  onRecheck: () => void;
  /** Where each problem is fixed. A fix the host cannot open is shown without a button. */
  onFix: (fix: SendFix) => void;
  canFix: (fix: SendFix) => boolean;
  /** The send was told not to email, so a missing copy is a choice rather than a missing address. */
  emailSkipped?: boolean;
  /** Not registered on a network: the plan silently leaves the e-invoice out, so the summary says why. */
  notRegistered?: boolean;
}

/**
 * Read from GET /send-plan before anything is committed. The network goes first: the PDF copy is only emailed
 * once the network confirms delivery.
 */
export function SendPlanSummary({
  plan,
  loading,
  error,
  onRecheck,
  onFix,
  canFix,
  emailSkipped = false,
  notRegistered = false,
}: Props) {
  if (loading && !plan) {
    return (
      <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Checking where this invoice goes…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        <span>{error}</span>
        <Button variant="outline" size="sm" onClick={onRecheck}>
          <RefreshCw className="size-4" />
          Try again
        </Button>
      </div>
    );
  }

  if (!plan) return null;

  const problems = plan.problems ?? [];
  const eInvoice = plan.eInvoice;
  const email = plan.email;
  const whatsApp = plan.whatsApp;

  return (
    <div className="space-y-3">
      <div className="space-y-2 rounded-lg border bg-muted/30 px-4 py-3 text-sm">
        {eInvoice ? (
          <div className="flex items-start gap-3">
            <FileCheck2 className="mt-0.5 size-4 shrink-0 text-primary" />
            <div>
              <p className="font-medium">
                {eInvoice.status ? "On " : "Sent over "}{networkLabel(eInvoice.network)}
                {eInvoice.recipientAddress ? (
                  <>
                    {" "}to <span className="font-mono">{eInvoice.recipientAddress}</span>
                  </>
                ) : null}
              </p>
              <p className="text-xs text-muted-foreground">
                {eInvoice.status
                  ? `Already on ${networkLabel(eInvoice.network)} (${deliveryLabel(eInvoice.status)}). It is not sent again.`
                  : "This is the legal delivery. It goes first, and the invoice is locked once it is sent."}
              </p>
            </div>
          </div>
        ) : null}

        {email ? (
          <div className="flex items-start gap-3">
            <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium">
                {eInvoice ? "PDF copy emailed to " : "Emailed to "}
                {email.to}
                {email.cc && email.cc.length > 0 ? (
                  <span className="font-normal text-muted-foreground"> · cc {email.cc.join(", ")}</span>
                ) : null}
              </p>
              <p className="text-xs text-muted-foreground">
                {!eInvoice
                  ? "As soon as the invoice is sent, with the payment link."
                  : eInvoice.status === "Delivered" || eInvoice.status === "MadeAvailable"
                    ? "Straight away: the network has already confirmed the invoice."
                    : email.timing === "Now"
                      ? `Straight away, without waiting for ${networkLabel(eInvoice.network)} to confirm delivery.`
                      : `Once ${networkLabel(eInvoice.network)} confirms delivery. Reminders start then too.`}
              </p>
            </div>
          </div>
        ) : eInvoice ? (
          <div className="flex items-start gap-3 text-muted-foreground">
            <Mail className="mt-0.5 size-4 shrink-0" />
            <p>{emailSkipped ? "No PDF copy for this send, as chosen." : "No PDF copy: the customer has no email address."}</p>
          </div>
        ) : null}

        {whatsApp?.willSend ? (
          <div className="flex items-start gap-3">
            <WhatsAppIcon className="mt-0.5 size-4 shrink-0" />
            <div>
              <p className="font-medium">Sent on WhatsApp to {whatsApp.to}</p>
              <p className="text-xs text-muted-foreground">
                {email ? "With the payment link, alongside the email." : "With the payment link."}
              </p>
            </div>
          </div>
        ) : whatsApp ? (
          <div className="flex items-start gap-3 text-muted-foreground">
            <WhatsAppIcon className="mt-0.5 size-4 shrink-0" mono />
            <p>Not sent on WhatsApp. {whatsApp.reason}</p>
          </div>
        ) : null}

        {!eInvoice && !email && !whatsApp?.willSend && problems.length === 0 ? (
          <div className="flex items-start gap-3 text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0" />
            <p>Nothing is sent to the customer. The invoice is issued; share its PDF or payment link yourself.</p>
          </div>
        ) : null}

        {plan.emailOnlyReason ? (
          <div className="flex items-start gap-3 text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0" />
            <p>Not sent as an e-invoice: {plan.emailOnlyReason}</p>
          </div>
        ) : !eInvoice && notRegistered ? (
          <div className="flex items-center justify-between gap-3 text-muted-foreground">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 size-4 shrink-0" />
              <p>Not sent as an e-invoice: this organisation is not registered for e-invoicing.</p>
            </div>
            {canFix("Organisation") ? (
              <Button variant="outline" size="sm" className="shrink-0" onClick={() => onFix("Organisation")}>
                {FIX_LABEL.Organisation}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {problems.length > 0 ? (
        <div className="space-y-2 rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <p className="flex items-center gap-2 font-medium text-destructive">
              <AlertTriangle className="size-4" />
              {problems.length === 1 ? "Fix this before sending" : `Fix these ${problems.length} things before sending`}
            </p>
            <Button variant="outline" size="sm" onClick={onRecheck} disabled={loading}>
              {loading ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
              Check again
            </Button>
          </div>
          <ul className="space-y-2">
            {problems.map((problem) => (
              <li key={problem.code} className="flex items-start justify-between gap-3">
                <span className="text-destructive">{problem.message}</span>
                {problem.fix && canFix(problem.fix) ? (
                  <Button variant="outline" size="sm" className="shrink-0" onClick={() => onFix(problem.fix!)}>
                    {FIX_LABEL[problem.fix]}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

type EInvoiceChoice = components["schemas"]["InvoiceEInvoiceChoice"];
type EmailChoice = components["schemas"]["InvoiceEmailChoice"];

interface ChoicesProps {
  plan: SendPlan | null;
  eInvoice: EInvoiceChoice;
  email: EmailChoice;
  onEInvoiceChange: (choice: EInvoiceChoice) => void;
  onEmailChange: (choice: EmailChoice) => void;
}

/**
 * Each change re-reads the plan. Skipping the network only where the law allows; emailing the PDF before the
 * verdict never for SDI, whose verdict decides whether the invoice legally exists.
 */
export function SendChannelChoices({ plan, eInvoice, email, onEInvoiceChange, onEmailChange }: ChoicesProps) {
  // The plan for a skipped network has no e-invoice in it, so the label keeps the network it last named.
  const [lastNetwork, setLastNetwork] = useState<string | null>(null);
  const planned = plan?.eInvoice?.network ?? null;
  if (planned && planned !== lastNetwork) setLastNetwork(planned);
  if (!plan) return null;

  const network = plan.eInvoice && !plan.eInvoice.status ? plan.eInvoice : null;
  const showNetworkChoice = (network !== null && !network.required) || eInvoice === "Skip";
  const showEmailChoice = !!plan.email || email === "Skip";
  const emailNowAllowed = plan.eInvoice?.network !== "Sdi";
  if (!showNetworkChoice && !showEmailChoice) return null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border px-4 py-3 text-sm">
      {showNetworkChoice ? (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="size-4"
            checked={eInvoice === "Auto"}
            onChange={(e) => onEInvoiceChange(e.target.checked ? "Auto" : "Skip")}
          />
          Send over {networkLabel(network?.network ?? lastNetwork)}
          {eInvoice === "Skip" ? <span className="text-muted-foreground">(email only for this send)</span> : null}
        </label>
      ) : null}
      {showEmailChoice ? (
        <label className="flex items-center gap-2">
          <Mail className="size-4 text-muted-foreground" />
          <span>PDF copy</span>
          <select
            className="rounded-md border bg-background px-2 py-1 text-sm"
            value={email}
            onChange={(e) => onEmailChange(e.target.value as EmailChoice)}
          >
            <option value="AfterNetwork">{plan.eInvoice ? "once the network confirms delivery" : "now"}</option>
            {plan.eInvoice && emailNowAllowed ? <option value="Now">now, without waiting</option> : null}
            <option value="Skip">don't email</option>
          </select>
        </label>
      ) : null}
    </div>
  );
}
